import { FilterTextField, ClearFiltersButton } from "../../../common/ClearControls";
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Backdrop,
  CircularProgress
} from '@mui/material';
import MainCard from '../../components/MainCard';
import Sidebar from "../../menu-items/Sidebar";
import axios from 'axios';
import { Autocomplete } from '@mui/material';
import Cookies from "js-cookie";

const ActivityReport = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);                 // MUI 0-based
  const [rowsPerPage, setRowsPerPage] = useState(150);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);   // loader for ALL export

  const [consentBundles, setConsentBundles] = useState([]);
  const [selectedConsentBundle, setSelectedConsentBundle] = useState('');

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');

  const [patient, setPatient] = useState([]);
  const [selectedpatient, setSelectedPatient] = useState('');

  const [practices, setPractices] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState('');

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // --- dropdowns ---
  useEffect(() => {
    (async () => {
      try {
        const token = axios.defaults.headers.common.Authorization;
        const baseUrl = process.env.REACT_APP_BASE_URL;

        const [bundlesRes, patientsRes, usersRes, practicesRes] = await Promise.all([
          axios.get(`${baseUrl}admin/dropdown/consent_bundles`, { headers: { Authorization: `${token}` } }),
          axios.get(`${baseUrl}admin/dropdown/patients`,         { headers: { Authorization: `${token}` } }),
          axios.get(`${baseUrl}admin/dropdown/users`,            { headers: { Authorization: `${token}` } }),
          axios.get(`${baseUrl}admin/dropdown/practices`,        { headers: { Authorization: `${token}` } }),
        ]);

        setConsentBundles(bundlesRes.data ?? []);
        setPatient(patientsRes.data ?? []);
        setUsers(usersRes.data ?? []);
        setPractices(practicesRes.data ?? []);
      } catch (err) {
        console.error('Dropdowns load failed:', err);
      }
    })();
  }, []);

  // --- core fetch with server-side pagination + filters ---
  const fetchData = async () => {
    const token = axios.defaults.headers.common.Authorization;
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const params = new URLSearchParams({
      limit: rowsPerPage.toString(),
      offset: (page + 1).toString(), // API is 1-based
    });

    if (selectedConsentBundle) params.append('consentBundle', selectedConsentBundle);
    if (selectedUser)          params.append('user', selectedUser);
    if (selectedPractice)      params.append('practice', selectedPractice);
    if (selectedpatient)       params.append('patient', selectedpatient);
    if (dateFrom)              params.append('date_from', dateFrom);
    if (dateTo)                params.append('date_to', dateTo);

    try {
      const response = await axios.get(`${baseUrl}admin/reports/activity_report?${params.toString()}`, {
        headers: { Authorization: `${token}` },
      });
      const resData = response.data ?? {};
      setData(resData.results || []);
      setTotalCount(resData.total || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
      setTotalCount(0);
    }
  };

  useEffect(() => { fetchData(); }, [page, rowsPerPage]);

  // when filters change, reset to page 0 then fetch
  useEffect(() => {
    setPage(0);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConsentBundle, selectedUser, selectedPractice, selectedpatient, dateFrom, dateTo]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // --- CSV current page ---
  const downloadPageAsCSV = () => {
    const headers = ['#', 'Event ID', 'User', 'Event', 'Event Type', 'Description', 'Practice', 'Timestamp', 'Consent bundle code', 'Patient Name'];
    const csvRows = [headers.join(',')];

    data.forEach((row, index) => {
      const cells = [
        (page * rowsPerPage + index + 1).toString(),
        row?.['event id'] ?? '',
        row?.user ?? '',
        row?.event ?? '',
        row?.eventType ?? '',
        row?.description ?? '',
        row?.practice ?? '',
        row?.timestamp ?? '',
        row?.consent_bundle_code ?? '',
        row?.patient_name ?? '',
      ].map(v =>
        typeof v === 'string' && (v.includes(',') || v.includes('"'))
          ? `"${v.replace(/"/g, '""')}"`
          : v
      );
      csvRows.push(cells.join(','));
    });

    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Activity_Report_Page.csv';
    link.click();
  };

  // --- helpers for ALL export ---
  const normalizePayload = (payload) => {
    const results = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
      ? payload
      : Object.values(payload ?? {});
    const total =
      typeof payload?.total === 'number'
        ? payload.total
        : Array.isArray(payload?.results)
        ? payload.results.length
        : Array.isArray(payload?.data)
        ? payload.data.length
        : Array.isArray(payload)
        ? payload.length
        : results.length;
    return { results, total };
  };

  // fetch one page for ALL export, honoring current filters
  const fetchPageAll = async (pageIndex1Based, limit) => {
    const token = axios.defaults.headers.common.Authorization;
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(pageIndex1Based),
    });
    if (selectedConsentBundle) params.append('consentBundle', selectedConsentBundle);
    if (selectedUser)          params.append('user', selectedUser);
    if (selectedPractice)      params.append('practice', selectedPractice);
    if (selectedpatient)       params.append('patient', selectedpatient);
    if (dateFrom)              params.append('date_from', dateFrom);
    if (dateTo)                params.append('date_to', dateTo);

    const res = await axios.get(`${baseUrl}admin/reports/activity_report?${params.toString()}`, {
      headers: { Authorization: `${token}` },
    });
    return normalizePayload(res.data);
  };

  // --- CSV ALL pages ---
  const downloadAllAsCSV = async () => {
    setExporting(true);
    try {
      const pageSize = 500; // adjust to backend cap
      const first = await fetchPageAll(1, pageSize);
      const allRows = [...first.results];
      const total = first.total;
      const totalPages = Math.ceil(total / pageSize);

      for (let p = 2; p <= totalPages; p++) {
        const { results } = await fetchPageAll(p, pageSize);
        allRows.push(...results);
      }

      const headers = ['#', 'Event ID', 'User', 'Event', 'Event Type', 'Description', 'Practice', 'Timestamp', 'Consent bundle code', 'Patient Name'];
      const csvRows = [headers.join(',')];

      allRows.forEach((row, idx) => {
        const cells = [
          (idx + 1).toString(),
          row?.['event id'] ?? '',
          row?.user ?? '',
          row?.event ?? '',
          row?.eventType ?? '',
          row?.description ?? '',
          row?.practice ?? '',
          row?.timestamp ?? '',
          row?.consent_bundle_code ?? '',
          row?.patient_name ?? '',
        ].map(v =>
          typeof v === 'string' && (v.includes(',') || v.includes('"'))
            ? `"${v.replace(/"/g, '""')}"`
            : v
        );
        csvRows.push(cells.join(','));
      });

      const csvData = csvRows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Activity_Report_ALL.csv';
      link.click();
    } catch (err) {
      console.error('Download ALL CSV failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const displayedData = data;

  return (
    <Box sx={{ border: '1px solid #ccc', padding: 5, height: '100vh' }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.75}>
        <Grid item xs={12} md={3} lg={2}>
          <Sidebar />
        </Grid>

        <Grid item xs={12} md={9} lg={10}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
              Activity Report
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" color="primary" onClick={downloadPageAsCSV} sx={{ marginLeft: 2 }}>
                Download Page CSV
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ marginLeft: 1 }}
                onClick={downloadAllAsCSV}
                disabled={exporting}
                startIcon={exporting ? <CircularProgress size={18} /> : null}
              >
                {exporting ? 'Preparing CSV…' : 'Download ALL CSV'}
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={consentBundles}
              getOptionLabel={(option) => option.label || ''}
              value={consentBundles.find((bundle) => bundle.value === selectedConsentBundle) || null}
              onChange={(_e, v) => setSelectedConsentBundle(v ? v.value : '')}
              renderInput={(params) => <TextField {...params} label="Consent Bundle" size="small" sx={{ minWidth: 250 }} />}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
            <Autocomplete
              options={[{ label: 'All', value: '' }, ...users]}
              getOptionLabel={(option) => option.label || ''}
              value={users.find((user) => user.value === selectedUser) || { label: 'All', value: '' }}
              onChange={(_e, v) => setSelectedUser(v ? v.value : '')}
              renderInput={(params) => <TextField {...params} label="Clinician" size="small" sx={{ minWidth: 250 }} />}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
            <Autocomplete
              options={[{ label: 'All', value: '' }, ...patient]}
              getOptionLabel={(option) => option.label || ''}
              value={patient.find((u) => u.value === selectedpatient) || { label: 'All', value: '' }}
              onChange={(_e, v) => setSelectedPatient(v ? v.value : '')}
              renderInput={(params) => <TextField {...params} label="Patients" size="small" sx={{ minWidth: 250 }} />}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
            <Autocomplete
              options={[{ label: 'All', value: '' }, ...practices]}
              getOptionLabel={(option) => option.label || ''}
              value={practices.find((practice) => practice.value === selectedPractice) || { label: 'All', value: '' }}
              onChange={(_e, v) => setSelectedPractice(v ? v.value : '')}
              renderInput={(params) => <TextField {...params} label="Practice" size="small" sx={{ minWidth: 250 }} />}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
            <FilterTextField active={dateFrom !== ""} onClear={() => setDateFrom("")} clearLabel="Clear date from filter"
              label="Date From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <FilterTextField active={dateTo !== ""} onClear={() => setDateTo("")} clearLabel="Clear date to filter"
              label="Date To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
                  <ClearFiltersButton onClear={() => { setSelectedConsentBundle(""); setSelectedUser(""); setSelectedPatient(""); setSelectedPractice(""); setDateFrom(""); setDateTo(""); setPage(0); }} />
          </Box>

          <MainCard title="">
            <TablePagination
              rowsPerPageOptions={[50, 100, 150, 200]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <TableContainer component={Paper} sx={{ borderRadius: '15px', boxShadow: 3, overflow: 'hidden', marginTop: 2 }}>
              <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2', color: '#fff' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Consent bundle code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Event</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Event Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Practice</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Patient Name</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {displayedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { backgroundColor: '#f5f5f5', cursor: 'pointer' },
                          borderBottom: '1px solid #ddd'
                        }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{row?.timestamp}</TableCell>
                        <TableCell>{row?.consent_bundle_code}</TableCell>
                        <TableCell>{row?.event}</TableCell>
                        <TableCell>{row?.eventType}</TableCell>
                        <TableCell>{row?.description}</TableCell>
                        <TableCell>{row?.user}</TableCell>
                        <TableCell>{row?.practice}</TableCell>
                        <TableCell>{row?.patient_name}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[50, 100, 150, 200]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </MainCard>
        </Grid>
      </Grid>

      {/* Full-screen loader during ALL export */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={exporting}
      >
        <CircularProgress />
      </Backdrop>
    </Box>
  );
};

export default ActivityReport;
